import clsx from 'clsx'
import { ButtonLink } from '@/components/carbon/Button'
import { IconExternal, IconInfo } from '@/components/carbon/icons'

interface NistDisclaimerProps {
  /** Whether to show as collapsible (default: false shows full content) */
  collapsible?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * NIST SP 800-53 disclaimer explaining the scope of Guardian's coverage, as a
 * Carbon low-contrast info notification (DESIGN.md "Inline notification"):
 * layer-01, a 3px leading bar and a 20px info icon in the neutral info tone
 * (D17), a 600 title and secondary body text. It is a block, not an
 * InlineNotification, because it holds a list and a link.
 *
 * Key points:
 * - Guardian covers automated technical controls via Microsoft Graph API
 * - NIST SP 800-53 Rev 5 has 1,000+ controls including organizational/physical
 * - Microsoft Purview Compliance Manager provides comprehensive coverage
 * - Guardian complements Purview with continuous automated monitoring
 */
export default function NistDisclaimer({ className = '' }: NistDisclaimerProps) {
  return (
    <aside
      aria-label="About NIST SP 800-53 coverage"
      className={clsx(
        'relative flex items-start gap-4 border border-[color:color-mix(in_oklab,var(--info)_40%,transparent)] bg-layer-1 py-3.5 pl-4 pr-4 text-body',
        className,
      )}
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-[color:var(--info)]" />
      <IconInfo size={20} className="flex-none text-[color:var(--info)]" />
      <div className="min-w-0 flex-1">
        <h4 className="m-0 text-body font-semibold text-text-primary">About NIST SP 800-53 coverage</h4>
        <p className="m-0 mt-1 max-w-[72ch] text-text-secondary">
          Guardian&rsquo;s NIST SP 800-53 coverage focuses on <b className="font-semibold text-text-primary">automated technical controls</b>{' '}
          that Guardian can evaluate through Microsoft Graph. NIST SP 800-53 Rev 5 holds more than 1,000 controls across
          technical, operational and management categories, and many of them need organizational processes, documents or
          physical controls that no tool can assess on its own.
        </p>
        <p className="m-0 mt-3 text-text-secondary">For a full NIST 800-53 assessment, pair Guardian with Microsoft Purview Compliance Manager:</p>
        <ul className="m-0 mt-1 list-disc pl-5 text-helper text-text-helper">
          <li>Built-in NIST SP 800-53 assessment templates</li>
          <li>Tracks both Microsoft-managed and customer-managed controls</li>
          <li>Includes organizational controls that need manual attestation</li>
        </ul>
        <ButtonLink
          href="https://compliance.microsoft.com/compliancemanager"
          kind="ghost"
          size="sm"
          icon={<IconExternal />}
          className="-ml-3 mt-2"
        >
          Microsoft Purview Compliance Manager
        </ButtonLink>
        <p className="m-0 mt-2 text-helper text-text-helper">
          Guardian adds continuous, automated monitoring of the technical settings. Purview carries the full compliance
          picture, including policies, procedures and attestations.
        </p>
      </div>
    </aside>
  )
}
